import PropTypes from "prop-types";
import { useState } from "react";
import { useFormikContext } from "formik";
import {
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  InputLabel
} from "@mui/material";
import {
  getSelectStyles,
  labelStyles,
  formControlStyles,
  helperTextStyles
} from "./component-styles";
import "./readonly-styles.css";
import useTranslate from "hooks/use-translate-language";

const getPath = name => name.split(".");

const getNestedValue = (obj, path) => {
  return path.reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

const SelectInput = ({
  label,
  options,
  name,
  showPleaseSelect = false,
  ...rest
}) => {
  const { values, handleBlur, handleChange, errors, touched } =
    useFormikContext();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const namePath = getPath(name);
  const isTouched = getNestedValue(touched, namePath);
  const errorMessage = getNestedValue(errors, namePath);
  const value = getNestedValue(values, namePath);

  const error = isTouched && errorMessage;
  const errorId = `${name}-error`;

  const handleSelectChange = e => {
    handleChange(e);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Combine existing aria-describedby with error ID
  const getAriaDescribedBy = () => {
    const existingDescribedBy = rest["aria-describedby"];
    if (error && existingDescribedBy) {
      return `${errorId} ${existingDescribedBy}`;
    } else if (error) {
      return errorId;
    }
    return existingDescribedBy;
  };

  // eslint-disable-next-line no-unused-vars
  const { "aria-describedby": _ariaDescribedBy, ...restWithoutAriaDescribedBy } = rest;

  return (
    <FormControl
      variant="outlined"
      sx={formControlStyles}
      fullWidth
      error={!!error}
    >
      <InputLabel
        id={`${name}-label`}
        sx={{
          ...labelStyles,
          ...(!!error && { color: "#b3261e" })
        }}
        shrink
      >
        {label}
      </InputLabel>
      <Select
        labelId={`${name}-label`}
        aria-labelledby={`${name}-label`}
        aria-describedby={getAriaDescribedBy()}
        aria-invalid={!!error}
        aria-expanded={open}
        aria-controls={`${name}-listbox`}
        aria-haspopup="listbox"
        aria-autocomplete="none"
        id={name}
        name={name}
        value={value || ""}
        onChange={handleSelectChange}
        onBlur={handleBlur}
        onOpen={handleOpen}
        onClose={handleClose}
        open={open}
        sx={getSelectStyles(!!error)}
        label={label}
        notched
        MenuProps={{
          id: `${name}-listbox`,
          role: "listbox"
        }}
        {...restWithoutAriaDescribedBy}
      >
        {showPleaseSelect && (
          <MenuItem value="">{translate("pleaseSelect")}</MenuItem>
        )}
        {options.map(({ text, value: optionValue }) => (
          <MenuItem
            key={optionValue}
            value={optionValue}
            aria-selected={value === optionValue}
          >
            {text}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText id={errorId} sx={helperTextStyles}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

SelectInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  showPleaseSelect: PropTypes.bool
};

export default SelectInput;
